<script setup lang="ts">
import { computed } from "vue";
import AnalysisFrame from "./AnalysisFrame.vue";
import { useAnalysisFrames } from "@/composable/useAnalysisFrames";
import { usePlanAnalysisState } from "@/stores/PlanAnalysisState";

/**
 * Surcouche HTML des cadres d'analyse, posée DANS le conteneur du canvas :
 * elle en épouse la géométrie, les coordonnées écran du viewer s'y appliquent
 * donc telles quelles. Visible seulement devant l'onglet IA du volet
 * Observations (et si les cadres ne sont pas masqués), pour ne pas polluer
 * le terrain.
 * Rien n'est écrit dans le .dwg.
 */

const state = usePlanAnalysisState();
const { frames, vignetteFrames } = useAnalysisFrames();

const visible = computed(
  () =>
    state.hasAnalysis &&
    state.isPanelOpen &&
    (state.framesVisible || state.zonesVisible),
);

/**
 * Projecteur sur le groupe courant : un voile assombrit tout le plan SAUF
 * son cadre (ombre géante autour d'un trou). Pur CSS — zéro toucher au
 * moteur ODA, les entités du groupe ressortent sans modifier le dessin.
 */
const spotlightFrame = computed(() =>
  state.framesVisible
    ? (frames.value.find((f) => f.isCurrent) ?? null)
    : null,
);

// Le badge rend le groupe courant : la liste de l'onglet IA défile d'elle-même
// jusqu'à sa carte.
const handleSelect = (groupId: string): void => {
  state.setCurrentGroup(groupId);
};
</script>

<template>
  <div v-if="visible" class="analysis-frames-overlay">
    <!-- Voile « projecteur » : tout est assombri sauf le groupe courant. -->
    <div
      v-if="spotlightFrame"
      class="spotlight"
      :style="{
        left: `${spotlightFrame.left}px`,
        top: `${spotlightFrame.top}px`,
        width: `${spotlightFrame.width}px`,
        height: `${spotlightFrame.height}px`,
      }"
    />
    <!-- Contours des vignettes détectées : comment la feuille a été découpée. -->
    <div
      v-for="vignette in state.zonesVisible ? vignetteFrames : []"
      :key="vignette.id"
      class="vignette-frame"
      :style="{
        left: `${vignette.left}px`,
        top: `${vignette.top}px`,
        width: `${vignette.width}px`,
        height: `${vignette.height}px`,
      }"
    >
      <span class="vignette-label">
        {{ vignette.name ?? vignette.id }}
      </span>
    </div>
    <AnalysisFrame
      v-for="frame in state.framesVisible ? frames : []"
      :key="frame.id"
      :frame="frame"
      @select="handleSelect"
    />
  </div>
</template>

<style lang="scss" scoped>
.analysis-frames-overlay {
  position: absolute;
  inset: 0;
  overflow: hidden;
  // Les cadres gèrent eux-mêmes leurs zones interactives (badges).
  pointer-events: none;
}

.spotlight {
  position: absolute;
  border-radius: 6px;
  // L'ombre démesurée fait le voile ; le trou, c'est l'élément lui-même.
  box-shadow: 0 0 0 100000px rgba(8, 6, 18, 0.42);
  pointer-events: none;
}

.vignette-frame {
  position: absolute;
  border: 1px dashed color-mix(in srgb, var(--dr-report-light) 55%, transparent);
  border-radius: 4px;
  pointer-events: none;
}

.vignette-label {
  position: absolute;
  top: -1px;
  left: -1px;
  padding: 1px 6px;
  font-size: 0.68rem;
  color: var(--dr-report-light);
  background: color-mix(in srgb, var(--dr-report) 20%, transparent);
  border-radius: 4px 0 4px 0;
  white-space: nowrap;
}
</style>
